import Check from '../Check';
import Report from '../Report';
import { MessageType } from '../Message';
import type { IMultiplePropagationConstraint } from '../../../stores/model/metamodel/multiple_propagation_constraint';
import type { IPath } from '../../../stores/model/metamodel/path';

function validPath(p: IPath): boolean {
  if (p == null || p.elements == null || p.elements.length === 0) {
    return false;
  }
  for (let i = 1; i < p.elements.length; ++i) {
    const previous = p.elements[i - 1];
    const current = p.elements[i];
    if (previous == null || current == null || previous.nextObject !== current.previousObject) {
      return false;
    }
  }
  return true;
}

/**
 * Check if the paths in an MPC are valid.
 */
class MPCPathsCheck extends Check<IMultiplePropagationConstraint> {
  static description = 'Check paths for a multiple propagation constraint';

  check(): Report {
    this.report = new Report();

    const validPrimaryPath = validPath(this.element.primaryPath);
    const validSecondaryPath = validPath(this.element.secondaryPath);

    if (!validPrimaryPath) {
      this.report.addMessage(
        MessageType.ERROR,
        'Primary path is empty or broken.',
        this,
        this.element
      );
    }

    if (!validSecondaryPath) {
      this.report.addMessage(
        MessageType.ERROR,
        'Secondary path is empty or broken.',
        this,
        this.element
      );
    }

    if (validPrimaryPath && validSecondaryPath) {
      if (this.element.primaryPath.equals(this.element.secondaryPath)) {
        this.report.addMessage(
          MessageType.ERROR,
          'Primary and secondary path are equal.',
          this,
          this.element
        );
      } else {
        this.report.addMessage(
          MessageType.INFORMATION,
          'Primary and secondary path are distinct.',
          this,
          this.element
        );
      }

      if (this.element.primaryPath.firstObject === this.element.secondaryPath.firstObject) {
        this.report.addMessage(
          MessageType.INFORMATION,
          'Primary and secondary path point to the same object.',
          this,
          this.element
        );
      } else {
        this.report.addMessage(
          MessageType.ERROR,
          `Primary and secondary path don't point to the same object.`,
          this,
          this.element
        );
      }

      if (this.element.primaryPath.lastObject === this.element.secondaryPath.lastObject) {
        this.report.addMessage(
          MessageType.INFORMATION,
          'Primary and secondary path start at the same object.',
          this,
          this.element
        );
      } else {
        this.report.addMessage(
          MessageType.ERROR,
          `Primary and secondary path don't start at the same object.`,
          this,
          this.element
        );
      }
    }

    return this.report;
  }
}

export default MPCPathsCheck;
